import * as React from 'react';
import { useEffect, useState } from 'react';
import type { ReservationState } from '@digitalopsdev/serviceops-reservations-components/dist/EditReservation/types';
import { PaymentTermsEditorComponent } from './PaymentTermsEditorComponent';

interface Props {
    reservationState: ReservationState;
    onClose: () => void;
}

export function BookingEngineDialog({ reservationState, onClose }: Props) {
    const reservationId = reservationState.ReservationId;

    const [link, setLink] = useState<string | null>(null);
    const [linkStatus, setLinkStatus] = useState<'loading' | 'ready' | 'error'>('loading');
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!reservationId) {
            setLinkStatus('error');
            return;
        }
        (async () => {
            try {
                const resp = await fetch(`/reservations/${reservationId}/booking-engine-link`);
                if (!resp.ok) {
                    setLinkStatus('error');
                    return;
                }
                const data = await resp.json();
                setLink(data.url);
                setLinkStatus('ready');
            } catch {
                setLinkStatus('error');
            }
        })();
    }, [reservationId]);

    const copyLink = async () => {
        if (!link) return;
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        } catch {
            // navigator.clipboard requires HTTPS / localhost — silent on failure.
        }
    };

    return (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            <i className="fa-solid fa-globe me-2"></i>
                            Booking Engine
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <section>
                            <h6 style={{ marginBottom: 8 }}>Guest Booking Link</h6>
                            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>
                                Send this link to the guest to open the booking engine for this reservation.
                            </p>
                            {linkStatus === 'loading' && <div style={{ fontSize: 13 }}>Loading link...</div>}
                            {linkStatus === 'error' && (
                                <div style={{ padding: 8, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 13 }}>
                                    Could not load the booking engine link.
                                </div>
                            )}
                            {linkStatus === 'ready' && link && (
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <input
                                        type="text"
                                        readOnly
                                        value={link}
                                        onFocus={(e) => e.currentTarget.select()}
                                        className="form-control"
                                        style={{ flex: '1 1 320px', fontSize: 13 }}
                                    />
                                    <button
                                        className="btn btn-outline-contrast"
                                        onClick={() => window.open(link, '_blank', 'noopener,noreferrer')}
                                    >
                                        <i className="fa-solid fa-arrow-up-right-from-square me-1"></i>
                                        Open
                                    </button>
                                    <button className="btn btn-outline-contrast" onClick={copyLink}>
                                        <i className={`fa-solid ${copied ? 'fa-check' : 'fa-copy'} me-1`}></i>
                                        {copied ? 'Copied' : 'Copy Link'}
                                    </button>
                                </div>
                            )}
                        </section>

                        <hr style={{ margin: '20px 0' }} />

                        <section>
                            <PaymentTermsEditorComponent reservationState={reservationState} />
                        </section>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-contrast" onClick={onClose}>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
